import React, { Suspense, lazy, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import LandingPage from '@app/start'
import Footer from '@components/Footer'
import RegistrationPage from '@components/RegistrationPage'
import LoginPage from '@components/LoginPage'
import ForgotPasswordPage from '@components/ForgotPasswordPage'
import { Routes, Route, useLocation } from 'react-router-dom'
import ResetPasswordPage from '@components/ResetPasswordPage'
import ActivationPage from '@components/ActivationPage'
import VerifyEmailPage from '@components/VerifyEmailPage'
import AcceptInvitePage from '@components/AcceptInvitePage'
import NotFoundPage from '@app/not-found/NotFoundPage'
import { DialogsCountersProvider } from '@app/DialogsCountersProvider'
import { GlobalTransferModal } from '@components/GlobalTransferModal'
import { SiteWsConnectionBanner } from '@components/SiteWsConnectionBanner'
import { SubscriptionNoticeBanner } from '@components/SubscriptionNoticeBanner'
import { WorkspaceAccessGate } from '@components/WorkspaceAccessGate'
import { EmailVerificationGate } from '@components/EmailVerificationGate'
import { Header } from '@components/Header'
import { ToastContainer } from '@/components/ToastContainer'
import { CookieConsent } from '@/components/CookieConsent'
import { useStore } from '@store/StoreProvider'
import { store } from '@store/Store'
import { handleUnauthorized } from '@utils/apiHelpers'
import { resetProfileTimezoneSync, syncBrowserTimezoneOnce } from '@/api/profileApi'
import { initNotificationAudioUnlock } from '@utils/notificationSounds'
import { syncSiteSupportWidget } from '@utils/siteSupportWidget'
import { setFaviconBadge } from '@utils/faviconBadge'
import { appDomain } from '@utils/appDomain'
import { GlobalTooltip } from '@components/ui/GlobalTooltip'

// Pages load on demand: a visitor of the landing does not download the operator panel, and
// every page's code lands in its own file (first open fetches it, then it is cached).
const UsersPage = lazy(() => import('@app/UsersPage'))
const TeamPage = lazy(() => import('@app/TeamPage'))
const TeamMemberEditPage = lazy(() => import('@app/TeamMemberEditPage'))
const MyProfilePage = lazy(() => import('@app/MyProfilePage'))
const DialogsPage = lazy(() => import('@app/DialogsPage'))
const VisitorsPage = lazy(() => import('@app/VisitorsPage'))
const SettingsPage = lazy(() => import('@app/SettingsPage'))
const StatisticsPage = lazy(() => import('@app/StatisticsPage'))
const BillingPage = lazy(() => import('@app/BillingPage'))
const InvoicePage = lazy(() => import('@app/InvoicePage'))
const KnowledgeBasePage = lazy(() => import('@app/knowledge-base/KnowledgeBasePage'))
const KnowledgeBaseArticlePage = lazy(() => import('@app/knowledge-base/KnowledgeBaseArticlePage'))
const OfferPage = lazy(() => import('@app/legal/OfferPage'))
const PolicyPage = lazy(() => import('@app/legal/PolicyPage'))
const StatusPage = lazy(() => import('@app/status/StatusPage'))
const PaymentSuccessPage = lazy(() =>
  import('@app/payment/PaymentResultPage').then((m) => ({ default: m.PaymentSuccessPage })),
)
const PaymentFailPage = lazy(() =>
  import('@app/payment/PaymentResultPage').then((m) => ({ default: m.PaymentFailPage })),
)

/** While a page's code downloads (first visit only). The spinner fades in late, so fast loads show nothing. */
function RouteLoading() {
  return (
    <div className="route-loading" role="status" aria-busy="true">
      <span className="route-loading__spinner" />
    </div>
  )
}

const App: React.FC = observer(() => {
  const location = useLocation();
  const isStatusHost = Boolean(appDomain()) && window.location.hostname.toLowerCase() === `status.${appDomain().toLowerCase()}`;
  const { auth, i18n, ui, emailVerification } = useStore()
  void i18n.language
  void i18n.version
  const emailUnverified = emailVerification.blocked || auth.currentUser?.emailVerified === false

  useEffect(() => {
    void i18n.init()
  }, [i18n])

  useEffect(() => {
    if (!auth.isAuthenticated) {
      resetProfileTimezoneSync()
      store.operatorPresence.reset()
      store.operatorWorkspaceSettings.reset()
      store.userWidgets.reset()
      store.operatorTransfers.reset()
      store.billing.reset()
      store.emailVerification.reset()
      store.ui.clearVisitorsActiveNow()
      return
    }
    // Arm autoplay unlock on any click/key before the first WS notification.
    initNotificationAudioUnlock()
    if (!i18n.apiBaseUrl) return
    syncBrowserTimezoneOnce(
      i18n.apiBaseUrl,
      auth.accessToken,
      auth.authHeader,
      auth.currentUser?.id,
    )
    if (emailVerification.blocked || auth.currentUser?.emailVerified === false) return
    void store.operatorPresence.loadOnce(
      i18n.apiBaseUrl,
      auth.authHeader,
      auth.currentUser?.id,
    )
    void store.operatorWorkspaceSettings.loadOnce(i18n.apiBaseUrl, auth.authHeader)
    void store.userWidgets.ensureLoaded(i18n.apiBaseUrl, auth.authHeader)
    void store.billing.ensureLoaded(i18n.apiBaseUrl, auth.authHeader)
  }, [
    auth.isAuthenticated,
    auth.accessToken,
    auth.currentUser?.id,
    auth.currentUser?.emailVerified,
    emailVerification.blocked,
    i18n.apiBaseUrl,
  ])

  useEffect(() => {
    if (!auth.isAuthenticated || !i18n.apiBaseUrl) return
    if (emailUnverified) return
    if (ui.dialogsCountersLoadedOnce) return

    ui.setDialogsCountersLoadedOnce(true)

    const base = (i18n.apiBaseUrl || '').replace(/\/$/, '')
    const url = `${base}/api/operator/dialogs/counters`
    fetch(url, { headers: { ...auth.authHeader } })
      .then((res) => {
        if (handleUnauthorized(res)) return null
        return res.json()
      })
      .then((json: any) => {
        if (!json?.success || !json?.data) return
        const d = json.data
        const inbox = Math.max(0, Math.trunc(Number(d.inbox) || 0))
        const my = Math.max(0, Math.trunc(Number(d.my) || 0))
        const archive = Math.max(0, Math.trunc(Number(d.archive) || 0))
        ui.setInboxDialogsCount(inbox)
        ui.setMyDialogsCount(my)
        ui.setArchiveDialogsCount(archive)
      })
      .catch((err) => console.error('[App] Dialog counters fetch failed:', err))
  }, [
    auth.isAuthenticated,
    auth.accessToken,
    i18n.apiBaseUrl,
    ui,
    ui.dialogsCountersLoadedOnce,
    emailUnverified,
  ])

  // List of routes where Header should be hidden (landing "/" handled separately — startsWith("/") matches all paths)
  const hideHeaderRoutes = ["/login", "/register", "/reset", "/reset-password", "/activate", "/auth/verify-email", "/auth/accept-invite", "/success", "/fail"];

  // List of routes where Footer should be hidden
  const hideFooterRoutes = ["/dialogs", "/login", "/reset", "/register", "/auth/verify-email", "/success", "/fail"];

  const knownAppRoutePrefixes = [
    '/knowledge-base',
    '/offer',
    '/terms',
    '/policy',
    '/status',
    '/register',
    '/login',
    '/reset',
    '/reset-password',
    '/activate',
    '/auth/verify-email',
    '/auth/accept-invite',
    '/success',
    '/fail',
    '/dialogs',
    '/visitors',
    '/control',
    '/team',
    '/myProfile',
    '/settings',
    '/statistics',
    '/billing',
  ] as const;

  const isKnownAppRoute =
    location.pathname === '/' ||
    knownAppRoutePrefixes.some(
      (path) => location.pathname === path || location.pathname.startsWith(`${path}/`),
    );

  const isPublicMarketingPage =
    location.pathname === '/' ||
    location.pathname.startsWith('/knowledge-base') ||
    location.pathname === '/offer' ||
    location.pathname === '/terms' ||
    location.pathname === '/policy' ||
    location.pathname === '/status' ||
    !isKnownAppRoute;

  const shouldHideHeader =
    isPublicMarketingPage ||
    hideHeaderRoutes.some((path) => location.pathname.startsWith(path));
  const shouldHideFooter =
    isPublicMarketingPage ||
    hideFooterRoutes.some((path) => location.pathname.startsWith(path));
  const showAppBanners = !shouldHideHeader;

  // ChatX's own support bubble lives on every public site page — landing, knowledge base,
  // legal pages, 404 — for signed-in users too (an operator may need help while browsing
  // the site). Never on the status page/host and never inside the app itself; the auth
  // pages are excluded as well (reCAPTCHA badge sits in the same corner).
  const isStatusPage = location.pathname === '/status' || location.pathname.startsWith('/status/')
  const isPublicSitePage =
    location.pathname === '/' ||
    location.pathname.startsWith('/knowledge-base') ||
    location.pathname === '/offer' ||
    location.pathname === '/terms' ||
    location.pathname === '/policy' ||
    !isKnownAppRoute
  const showSiteWidget = !isStatusHost && !isStatusPage && isPublicSitePage

  useEffect(() => {
    syncSiteSupportWidget(showSiteWidget)
  }, [showSiteWidget])

  // Browser-tab icon counter: new inbox dialogs + accepted dialogs with an unopened visitor
  // message. Only for a signed-in operator, never on the status host.
  const faviconBadgeCount =
    auth.isAuthenticated && !isStatusHost ? (ui.inboxDialogsCount ?? 0) + (ui.attentionMyDialogsCount ?? 0) : 0
  useEffect(() => {
    setFaviconBadge(faviconBadgeCount)
  }, [faviconBadgeCount])

  useEffect(() => {
    try {
      const collapsed = localStorage.getItem('chatx_sidebar_collapsed') === '1'
      document.documentElement.dataset.sbCollapsed = collapsed ? 'true' : 'false'
    } catch {
      document.documentElement.dataset.sbCollapsed = 'false'
    }
    if (shouldHideHeader) {
      document.documentElement.setAttribute('data-theme', 'dark');
      return;
    }
    const saved = localStorage.getItem('chatx_theme');
    document.documentElement.setAttribute('data-theme', saved === 'light' ? 'light' : 'dark');
  }, [shouldHideHeader]);

  return (
    <>
      <DialogsCountersProvider>
        {showAppBanners && <SiteWsConnectionBanner />}
        <EmailVerificationGate>
          <WorkspaceAccessGate />
          {!shouldHideHeader && <Header />}
          {showAppBanners && <SubscriptionNoticeBanner />}
          <Suspense fallback={<RouteLoading />}>
          <Routes>
          <Route path="/" element={isStatusHost ? <StatusPage /> : <LandingPage />} />
          <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
          <Route path="/knowledge-base/:slug" element={<KnowledgeBaseArticlePage />} />
          <Route path="/offer" element={<OfferPage />} />
          <Route path="/terms" element={<OfferPage />} />
          <Route path="/policy" element={<PolicyPage />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/register" element={<RegistrationPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/activate" element={<ActivationPage />} />
          <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
          <Route path="/auth/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/dialogs" element={<DialogsPage />} />
          <Route path="/visitors" element={<VisitorsPage />} />
          <Route path="/control" element={<UsersPage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/team/:id/edit" element={<TeamMemberEditPage />} />
          <Route path="/myProfile" element={<MyProfilePage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/statistics" element={<StatisticsPage />} />
          <Route path="/billing/invoices/:invoiceId" element={<InvoicePage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/success" element={<PaymentSuccessPage />} />
          <Route path="/fail" element={<PaymentFailPage />} />
          <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </Suspense>
          {!shouldHideFooter && <Footer />}
          <GlobalTransferModal />
        </EmailVerificationGate>
        <ToastContainer />
        <CookieConsent />
        <GlobalTooltip />
      </DialogsCountersProvider>
    </>
  )
})

export default App

